import React from "react";
import { useTranslation } from "react-i18next";
import { Column, Row, ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/DataTable";
import {
  Button,
  MenubarContent,
  MenubarItem,
  Menubar,
  MenubarTrigger,
  MenubarMenu,
} from "@/components/ui";
import { MoreVertical } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import {
  DATE_FORMAT,
  PLACEHOLDER_FALLBACK,
  getAudienceLabel,
  getEventTypeLabel,
} from "./utils";
import { formatDate } from "@/lib/formatDate";
import { LiveEvent } from "@/__generated__/types";
import { DestroyEventButton } from "@/pages/GamesAndLiveEventsOverview/DestroyEventButton";
import { timestampToDate } from "@/util/dateUtils";

export const useGamesLiveEventsColumns = (): ColumnDef<LiveEvent>[] => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { navToRoute } = useProtectedNavigation();

  const handleDetailsClick = (event: LiveEvent) => {
    navToRoute("eventDetails", { eventId: event.id });
  };

  const columns: ColumnDef<LiveEvent>[] = [
    {
      id: "name",
      accessorFn: (row: LiveEvent) => row?.name || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesLiveEvents.communityToolsOverview.columns.eventName")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          <div className="w-80 overflow-hidden text-ellipsis">
            {row.original?.name || PLACEHOLDER_FALLBACK}
          </div>
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "eventType",
      accessorFn: (row: LiveEvent) => {
        if (!row) return "";
        return getEventTypeLabel(row.eventType);
      },
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesLiveEvents.communityToolsOverview.columns.eventType")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original
            ? getEventTypeLabel(row.original.eventType)
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "startTime",
      accessorFn: (row: LiveEvent) => {
        const { startsAt } = row || {};
        return startsAt
          ? formatDate(timestampToDate(startsAt * 1000), DATE_FORMAT, {
              locale,
            })
          : "";
      },
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesLiveEvents.communityToolsOverview.columns.startTime")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original?.startsAt
            ? formatDate(
                timestampToDate(row.original.startsAt * 1000),
                DATE_FORMAT,
                {
                  locale,
                },
              )
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "audience",
      accessorFn: (row: LiveEvent) => row?.audience || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesLiveEvents.communityToolsOverview.columns.audience")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {getAudienceLabel(row.original?.audience) || PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "chatChannel",
      accessorFn: (row: LiveEvent) => row?.chatChannel?.displayName || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesLiveEvents.communityToolsOverview.columns.chatChannel")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original?.chatChannel?.displayName || PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "actions",
      accessorFn: () => "",
      enableSorting: false,
      enableColumnFilter: false,
      header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
      cell: ({ row }: { row: Row<LiveEvent> }) => {
        const event = row.original;
        if (!event) {
          return (
            <DataTable.TBodyCell>{PLACEHOLDER_FALLBACK}</DataTable.TBodyCell>
          );
        }

        return (
          <DataTable.TBodyCell isLastCell>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDetailsClick(event)}
              >
                {t("gamesLiveEvents.communityToolsOverview.actions.details")}
              </Button>

              <Menubar className="p-0">
                <MenubarMenu>
                  <MenubarTrigger
                    role="menuitem"
                    aria-label={t("dataTable.openTableActionsMenu")}
                    aria-haspopup="true"
                    className="cursor-pointer p-2 hover:bg-muted"
                  >
                    <MoreVertical className="h-4 w-4" aria-hidden="true" />
                  </MenubarTrigger>
                  <MenubarContent
                    align="end"
                    role="menu"
                    data-testid="table-actions-menu"
                    aria-label={t("dataTable.tableActions")}
                    aria-orientation="vertical"
                  >
                    <MenubarItem
                      onClick={(e) => e.preventDefault()}
                      variant="destructive"
                      role="menuitem"
                      aria-label={t(
                        "gamesLiveEvents.communityToolsOverview.actions.delete",
                      )}
                    >
                      <DestroyEventButton event={event} />
                    </MenubarItem>
                  </MenubarContent>
                </MenubarMenu>
              </Menubar>
            </div>
          </DataTable.TBodyCell>
        );
      },
    },
  ];

  return columns;
};
